import { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Zap,
  Diamond,
  CheckCircle2,
  ArrowRight,
  Plus,
  Activity,
  Lock,
  UserCheck,
  PieChart,
  Shield,
  Database,
  FileText,
  BarChart3,
  MessageSquare,
} from 'lucide-react';
import { getAuthLink } from '@/lib/links';
import { HOME_AGENTS } from './homeData';

const WORKFLOW_SCENARIOS = [
  {
    id: 'storm-call',
    label: 'After-Hours Hail Call',
    triggerText: 'Homeowner calls after hours or storm damage detected',
    conditionText: 'Is inspection or replacement requested?',
    ashleyTask:
      'Answers call in 1.8s, captures roof symptoms, and syncs appointment to Google Calendar.',
    ashleyStatus: 'Appointment confirmed',
    tylerTask:
      'Measures satellite geometry, detects 7/12 pitch, and creates Good/Better/Best tiers.',
    tylerStatus: '34 SQ calculated',
    sofiaTask:
      'Collects material deposit in Stripe escrow and releases contractor draw on tear-off completion.',
    sofiaStatus: 'Deposit locked in escrow',
  },
  {
    id: 'web-quote',
    label: 'Instant Web Quote',
    triggerText: 'Homeowner enters street address on website estimator widget',
    conditionText: 'Does property match residential or commercial roof profile?',
    ashleyTask:
      'Places instant courtesy follow-up call and verifies homeowner insurance deductible.',
    ashleyStatus: 'Homeowner qualified',
    tylerTask:
      'Runs 3D aerial facet trace in 84 seconds and sends interactive 3-option proposal link.',
    tylerStatus: '3-tier quote delivered',
    sofiaTask: 'Generates digital e-sign contract with automatic 30% material deposit invoice.',
    sofiaStatus: 'Contract signed & funded',
  },
];

const GOVERNANCE_ITEMS = [
  {
    icon: Activity,
    title: 'Agent activity',
    description:
      'Track every answered call, satellite takeoff, and SMS reply in a real-time audit log.',
  },
  {
    icon: Lock,
    title: 'Permissions',
    description:
      'Decide who can deploy agents, modify pricing tiers, or approve milestone escrow draws.',
  },
  {
    icon: UserCheck,
    title: 'Human in the loop',
    description:
      'Require estimator sign-off on commercial bids above $50,000 before proposals go out.',
  },
  {
    icon: PieChart,
    title: 'AI cost center',
    description: 'Predictable flat-rate agent capacity with spend tracked per agent.',
  },
  {
    icon: Shield,
    title: 'Content ownership',
    description:
      'Every aerial measurement, jobsite photo, and customer transcript belongs 100% to you.',
  },
  {
    icon: Database,
    title: 'No data training',
    description:
      'Your proprietary contractor pricing, margins, and customer lists never train public models.',
  },
];

export function WorkflowMonday() {
  const shouldReduceMotion = useReducedMotion();
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [pulsePhase, setPulsePhase] = useState(0);

  const ashley = HOME_AGENTS.find((a) => a.id === 'ashley') || HOME_AGENTS[0];
  const tyler = HOME_AGENTS.find((a) => a.id === 'tyler') || HOME_AGENTS[1];
  const sofia = HOME_AGENTS.find((a) => a.id === 'sofia') || HOME_AGENTS[5];

  const activeScenario = WORKFLOW_SCENARIOS[scenarioIdx];

  useEffect(() => {
    if (shouldReduceMotion) return undefined;
    const timer = setInterval(() => {
      setPulsePhase((prev) => (prev + 1) % 3);
    }, 1800);
    return () => clearInterval(timer);
  }, [shouldReduceMotion]);

  return (
    <section className="py-20 sm:py-28 bg-white overflow-hidden">
      <div className="max-w-[1380px] mx-auto px-5 sm:px-8">
        {/* =====================================================================
            PART 1: Exact monday.com Slice 04-06 "Work in context" Editorial Stage
           ===================================================================== */}
        <div className="mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-7">
              <span className="text-xs sm:text-sm font-normal text-[#323338] block mb-3">
                Powered by your roofing data
              </span>
              <h2 className="text-5xl sm:text-7xl lg:text-[92px] font-medium tracking-[-0.04em] text-[#000000] leading-[0.96]">
                Work in
                <br />
                context
              </h2>
            </div>
            <div className="lg:col-span-5 pb-2">
              <p className="text-base sm:text-[18px] text-[#323338] leading-[1.55] mb-6 font-normal">
                Agents understand your business with full access to your job history, satellite
                imagery, supplier pricing, and crew calendars.
              </p>
              <a
                href={getAuthLink('/register')}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-[#000000] text-white text-sm font-medium hover:bg-[#181B20] transition-colors"
              >
                <span>Get Started</span>
                <ArrowRight className="size-4" />
              </a>
            </div>
          </div>

          {/* Real Rooflyzer OS Context Cards Surrounding Central Agent Portrait */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-center bg-[#FAFAFC] rounded-[36px] border border-gray-200/70 p-6 sm:p-10">
            {/* Left Context Cards (4 cols): Real Dashboard Captures */}
            <div className="md:col-span-4 space-y-4">
              {/* Card 1: Real Finance & Revenue Trend Capture */}
              <div className="bg-white rounded-2xl p-3.5 border border-gray-200/80 shadow-2xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-[#161616]">
                    Finance & Revenue Trend
                  </span>
                  <BarChart3 className="size-4 text-[#161616]" />
                </div>
                <div className="aspect-[16/10] rounded-xl overflow-hidden border border-gray-100 mb-2">
                  <img
                    src="/product-frames/reports.webp"
                    alt="Rooflyzer OS Finance and Invoice Revenue Trend capture"
                    className="w-full h-full object-cover object-left-top"
                  />
                </div>
                <span className="text-[11px] text-[#323338] font-medium block">
                  $13,450 Collected · $24,850 Total Billed
                </span>
              </div>

              {/* Card 2: Real 3D Roof Viewer Capture */}
              <div className="bg-white rounded-2xl p-3.5 border border-gray-200/80 shadow-2xs">
                <span className="text-xs font-semibold text-[#161616] block mb-2">
                  3D Roof Viewer & Line Check
                </span>
                <div className="aspect-[16/10] rounded-xl overflow-hidden border border-gray-100 mb-2">
                  <img
                    src="/product-frames/roof-3d.webp"
                    alt="Rooflyzer OS 3D Roof Viewer capture"
                    className="w-full h-full object-cover object-left-top"
                  />
                </div>
                <span className="text-[11px] text-[#323338] font-medium block">
                  4 Facets · 1,342 sq ft · 6/12 Hip Roof
                </span>
              </div>
            </div>

            {/* Centerpiece Agent Blueprint Portrait (4 cols) */}
            <div className="md:col-span-4 flex flex-col items-center">
              <div className="w-full max-w-[280px] rounded-[28px] overflow-hidden bg-white border border-gray-200/80 shadow-md p-3">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#F5F5F2] mb-3">
                  <img
                    src={tyler.portrait}
                    alt={tyler.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="px-1">
                  <span className="text-xs font-semibold text-[#000000] block">
                    {tyler.name} · Context Engine
                  </span>
                  <span className="text-[11px] text-[#676879]">
                    Synced with live Estimates, Leads, Pipeline & Invoices
                  </span>
                </div>
              </div>
            </div>

            {/* Right Context Cards (4 cols): Real Dashboard Captures */}
            <div className="md:col-span-4 space-y-4">
              {/* Card 3: Real Measured Roof Estimate Capture */}
              <div className="bg-white rounded-2xl p-3.5 border border-gray-200/80 shadow-2xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-[#161616]">
                    Measured Roof Estimate
                  </span>
                  <FileText className="size-4 text-[#161616]" />
                </div>
                <div className="aspect-[16/10] rounded-xl overflow-hidden border border-gray-100 mb-2">
                  <img
                    src="/product-frames/estimates.webp"
                    alt="Rooflyzer OS Measured Roof Estimate workspace capture"
                    className="w-full h-full object-cover object-left-top"
                  />
                </div>
                <span className="text-[11px] text-[#323338] font-medium block">
                  $14,993.23 Estimate Total · 2,683 sq ft · 29.5 Squares
                </span>
              </div>

              {/* Card 4: Real Leads & Pipeline Capture */}
              <div className="bg-white rounded-2xl p-3.5 border border-gray-200/80 shadow-2xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-[#161616]">
                    Leads & Sales Pipeline
                  </span>
                  <MessageSquare className="size-4 text-[#161616]" />
                </div>
                <div className="aspect-[16/10] rounded-xl overflow-hidden border border-gray-100 mb-2">
                  <img
                    src="/product-frames/leads.webp"
                    alt="Rooflyzer OS Leads workspace capture"
                    className="w-full h-full object-cover object-left-top"
                  />
                </div>
                <span className="text-[11px] text-[#323338] font-medium block">
                  $208,600 Pipeline Value · 8 Active Workspace Leads
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================================
            PART 2: Exact monday.com Slice 07 "Full control" 3x2 Governance Stage
           ===================================================================== */}
        <div className="bg-[#F3F4F5] rounded-[36px] p-8 sm:p-12 lg:p-16 mb-24">
          <div className="mb-12">
            <span className="text-xs sm:text-sm font-normal text-[#323338] block mb-2">
              Enterprise governance
            </span>
            <h2 className="text-5xl sm:text-7xl lg:text-[88px] font-medium tracking-[-0.04em] text-[#000000] leading-[0.98]">
              Full control
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
            {GOVERNANCE_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="flex flex-col">
                  <div className="size-10 rounded-xl bg-white text-[#7950A2] border border-[#E5DEEE] flex items-center justify-center mb-4 shadow-2xs">
                    <Icon className="size-5 stroke-[1.75]" />
                  </div>
                  <h4 className="text-lg font-medium text-[#000000] mb-1.5">{item.title}</h4>
                  <p className="text-sm text-[#323338] leading-relaxed font-normal">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* =====================================================================
            PART 3: Exact monday.com Slice 10-11 "Let work flow" Workflow Tree
           ===================================================================== */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="text-center max-w-4xl mx-auto mb-12"
        >
          <p className="text-xs sm:text-sm font-medium uppercase tracking-widest text-[#7950A2] mb-3">
            Autonomous workflows that run your office
          </p>

          <h2 className="text-5xl sm:text-7xl lg:text-[96px] font-medium tracking-[-0.04em] text-[#000000] leading-[0.98] mb-6">
            Let work flow
          </h2>

          {/* Inline Human + Agent Avatars Sentence (Exact monday-slice-10.png match) */}
          <p className="text-lg sm:text-2xl font-normal text-[#181B20] flex flex-wrap items-center justify-center gap-2.5 leading-snug">
            <span>Automate how work moves between</span>
            <span className="inline-flex items-center -space-x-2 px-1">
              {HOME_AGENTS.slice(0, 3).map((a) => (
                <img
                  key={`crew-${a.id}`}
                  src={a.portrait}
                  alt=""
                  className="size-8 rounded-full object-cover ring-2 ring-white border border-gray-200"
                />
              ))}
            </span>
            <span>roofing teams and agents</span>
            <span className="inline-flex items-center -space-x-2 px-1">
              {HOME_AGENTS.slice(3, 6).map((a) => (
                <img
                  key={`agent-${a.id}`}
                  src={a.portrait}
                  alt=""
                  className="size-8 rounded-full object-cover ring-2 ring-[#E5DEEE] border border-[#7950A2]"
                />
              ))}
            </span>
          </p>

          {/* Interactive Scenario Toggle */}
          <div className="mt-8 inline-flex items-center gap-2 p-1.5 rounded-full bg-[#F3F4F5] border border-gray-200/80">
            {WORKFLOW_SCENARIOS.map((scen, idx) => {
              const isSelected = scenarioIdx === idx;
              return (
                <button
                  key={scen.id}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => {
                    setScenarioIdx(idx);
                    setPulsePhase(0);
                  }}
                  className={`relative px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                    isSelected ? 'text-white' : 'text-[#323338] hover:text-[#000000]'
                  }`}
                >
                  {isSelected && (
                    <motion.span
                      layoutId="workflowScenarioPill"
                      transition={{ type: 'spring', stiffness: 360, damping: 28 }}
                      className="absolute inset-0 rounded-full bg-[#7950A2] shadow-2xs"
                    />
                  )}
                  <span className="relative z-10">{scen.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Exact monday.com Slice 10-11 Vertical Workflow Tree */}
        <div className="max-w-4xl mx-auto flex flex-col items-center pt-4">
          {/* 1. Trigger Card with Green Lightning Hexagon Badge */}
          <motion.div
            animate={{
              scale: pulsePhase === 0 ? 1.02 : 1,
            }}
            className="w-full max-w-md pt-6 pb-5 px-6 rounded-[24px] bg-white border border-gray-200/90 shadow-[0_12px_35px_rgba(0,0,0,0.06)] text-center relative"
          >
            {/* Floating Green Lightning Badge */}
            <div className="absolute -top-4 left-6 size-9 rounded-xl bg-[#00C875] text-white flex items-center justify-center shadow-sm rotate-3">
              <Zap className="size-4 fill-current" />
            </div>
            <span className="text-sm sm:text-base font-normal text-[#181B20] block">
              {activeScenario.triggerText}
            </span>
          </motion.div>

          {/* Vertical Connector Line 1 */}
          <div className="w-0.5 h-10 bg-gray-300" />

          {/* 2. Tilted Rainbow-Bordered Condition Card with Purple Diamond Badge */}
          <motion.div
            animate={{
              scale: pulsePhase === 1 ? 1.03 : 1,
            }}
            className="w-full max-w-md p-[2px] rounded-[24px] bg-gradient-to-r from-[#7950A2] via-[#00C875] to-[#F7C76C] -rotate-1 shadow-[0_14px_40px_rgba(121,80,162,0.12)] relative"
          >
            <div className="bg-white rounded-[22px] py-5 px-6 text-center relative">
              <div className="absolute -top-4 left-6 size-9 rounded-xl bg-[#7950A2] text-white flex items-center justify-center shadow-sm">
                <Diamond className="size-4 fill-current" />
              </div>
              <span className="text-sm sm:text-base font-medium text-[#000000] block">
                {activeScenario.conditionText}
              </span>
            </div>
          </motion.div>

          {/* 3. Curved SVG Branching Tree with "Yes" / "Auto" / "Escrow" Pills */}
          <div className="w-full max-w-4xl h-16 relative flex items-center justify-center">
            <svg
              viewBox="0 0 600 64"
              preserveAspectRatio="none"
              className="w-full h-full overflow-visible hidden md:block"
              aria-hidden="true"
            >
              <path
                d="M300,0 C300,32 100,20 100,64 M300,0 L300,64 M300,0 C300,32 500,20 500,64"
                fill="none"
                stroke="#D1D5DB"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            <div className="hidden md:flex absolute inset-x-0 top-4 justify-around px-16 pointer-events-none">
              <span className="px-3 py-0.5 rounded-full bg-white border border-gray-200 text-[11px] font-medium text-[#323338] shadow-2xs">
                Yes · Intake
              </span>
              <span className="px-3 py-0.5 rounded-full bg-white border border-gray-200 text-[11px] font-medium text-[#323338] shadow-2xs">
                Yes · Takeoff
              </span>
              <span className="px-3 py-0.5 rounded-full bg-white border border-gray-200 text-[11px] font-medium text-[#323338] shadow-2xs">
                Yes · Escrow
              </span>
            </div>
            <div className="md:hidden w-0.5 h-10 bg-gray-300" />
          </div>

          {/* 4. Three Rainbow-Bordered Agent Action Cards + Bottom (+) Nodes */}
          <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                agent: ashley,
                title: `${ashley.name} · Intake`,
                task: activeScenario.ashleyTask,
                status: activeScenario.ashleyStatus,
              },
              {
                agent: tyler,
                title: `${tyler.name} · Estimator`,
                task: activeScenario.tylerTask,
                status: activeScenario.tylerStatus,
              },
              {
                agent: sofia,
                title: `${sofia.name} · Billing`,
                task: activeScenario.sofiaTask,
                status: activeScenario.sofiaStatus,
              },
            ].map((node) => (
              <div key={node.title} className="flex flex-col items-center">
                <motion.div
                  animate={{ y: pulsePhase === 2 ? -3 : 0 }}
                  className="w-full p-[2px] rounded-[26px] bg-gradient-to-br from-[#7950A2] via-[#E5DEEE] to-[#F7C76C] shadow-[0_12px_35px_rgba(0,0,0,0.06)]"
                >
                  <div className="bg-white rounded-[24px] p-5 h-full flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={node.agent.portrait}
                          alt={node.agent.name}
                          className="size-10 rounded-2xl object-cover border border-gray-200"
                        />
                        <div>
                          <span className="text-xs font-semibold text-[#000000] block">
                            {node.title}
                          </span>
                          <span className="text-[11px] text-[#7950A2] font-medium">
                            Autonomous Agent
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-[#323338] leading-relaxed">{node.task}</p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-1.5 text-[11px] font-medium text-[#008A50]">
                      <CheckCircle2 className="size-3.5 text-[#00C875]" />
                      <span>{node.status}</span>
                    </div>
                  </div>
                </motion.div>

                {/* Vertical Stem + (+) Node Button */}
                <div className="w-0.5 h-6 bg-gray-300" />
                <span className="size-7 rounded-full bg-white border border-gray-300 text-gray-500 flex items-center justify-center shadow-2xs">
                  <Plus className="size-3.5" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
